import React from "react";
import { Button, Card, Col, Container, Image, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MainBody from "../components/layout/MainBody";
import AppStoreBadges from "../components/AppStoreBadges";

const services = [
  {
    id: 0,
    name: "Mobile App Development",
    iconName: "fa-solid fa-mobile-screen-button",
    description:
      "Native and cross-platform apps for iOS and Android, from concept to the app stores.",
  },
  {
    id: 1,
    name: "Web Development",
    iconName: "fa-solid fa-laptop",
    description:
      "Modern, responsive websites and web applications built to grow with your business.",
  },
  {
    id: 2,
    name: "Hosting & Maintenance",
    iconName: "fa-regular fa-hard-drive",
    description:
      "Secure deployment, reliable hosting, and ongoing support so your software keeps working.",
  },
];

const clientLogos = [
  {
    id: 0,
    image: require("../assets/clients/vgu/vgu_logo.png"),
    alt: "VIP Global UK",
    height: 60,
  },
  {
    id: 1,
    image: require("../assets/clients/rhr/rhr_logo.png"),
    alt: "Right-Hand Roofing",
    height: 60,
  },
  {
    id: 2,
    image: require("../assets/clients/a1p/a1p_logo.png"),
    alt: "A1 Property Inspections of Georgia",
    height: 60,
  },
  {
    id: 3,
    image: require("../assets/clients/jgpg/jgpg_logo.png"),
    alt: "Johnson Global Publishing Group",
    height: 44,
  },
];

const featuredAppHighlights = [
  {
    id: 0,
    iconName: "fa-solid fa-house",
    text: "Listings, prices, and key dates in one place",
  },
  {
    id: 1,
    iconName: "fa-solid fa-dollar-sign",
    text: "Commission tracking for every deal",
  },
  {
    id: 2,
    iconName: "fa-solid fa-bell",
    text: "Closing-day reminders and calendar sync",
  },
  {
    id: 3,
    iconName: "fa-solid fa-lock",
    text: "Private by design — no accounts, no tracking",
  },
];

const Home = () => {
  const vvpImage = require("../assets/clients/vgu/vvp_2.png");
  const appStoreImage = require("../assets/mobile/getappstore.png");
  const playStoreImage = require("../assets/mobile/getgoogleplay.png");
  const reAssistImage = require("../assets/apps/reassist/reassist_dashboard.jpg");

  return (
    <>
      {/* Hero */}
      <MainBody>
        <Container style={styles.heroContainer}>
          <Row className="justify-content-center text-center">
            <Col xs={12} lg={9}>
              <h1 style={styles.heroTitle}>
                Custom software, designed and built for your business.
              </h1>
              <p style={styles.heroSubtitle}>
                Cross Vision Development Studios designs, builds, deploys, and
                maintains web and mobile applications — from first idea to
                launch and beyond.
              </p>
              <div style={styles.heroButtons}>
                <Button
                  variant="primary"
                  size="lg"
                  as={Link}
                  to="/contact"
                  style={styles.primaryCta}
                >
                  Start your project
                </Button>
                <Button
                  variant="outline-light"
                  size="lg"
                  as={Link}
                  to="/portfolio"
                  style={styles.secondaryCta}
                >
                  See our work
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </MainBody>

      {/* Featured app */}
      <Container fluid style={styles.sectionDarker}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={6} className="mb-4 mb-md-0">
              <p style={styles.eyebrow}>Featured app</p>
              <h2 style={styles.sectionTitleLeft}>Real Estate Agent Assist</h2>
              <p style={styles.featuredText}>
                Our own app for real estate agents, on iPhone and Android.
                Manage listings, track commissions, and never miss a closing
                date — with your data on your device or in your own cloud
                storage.
              </p>
              <div style={styles.highlightList}>
                {featuredAppHighlights.map((item) => (
                  <div key={item.id} style={styles.highlightRow}>
                    <FontAwesomeIcon
                      icon={item.iconName}
                      style={styles.highlightIcon}
                    />
                    <span style={styles.highlightText}>{item.text}</span>
                  </div>
                ))}
              </div>
              <div style={styles.storeBadges}>
                <AppStoreBadges height={44} align="left" />
              </div>
              <div style={styles.featuredButtons}>
                <Button
                  variant="primary"
                  as={Link}
                  to="/apps/real-estate-assist"
                  style={styles.primaryCta}
                >
                  Learn more
                </Button>
                <Button variant="outline-light" as={Link} to="/apps">
                  All our apps
                </Button>
              </div>
            </Col>
            <Col xs={12} md={6} className="text-center">
              <Image
                fluid
                src={reAssistImage}
                style={styles.featuredImage}
                alt="Real Estate Agent Assist dashboard screenshot"
              />
            </Col>
          </Row>
        </Container>
      </Container>

      {/* Services snapshot */}
      <Container fluid style={styles.sectionDark}>
        <Container>
          <Row className="text-center mb-5">
            <h2 style={styles.sectionTitle}>What we do</h2>
            <p style={styles.sectionSubtitle}>
              End-to-end software engineering under one roof.
            </p>
          </Row>
          <Row>
            {services.map((item) => (
              <Col key={item.id} xs={12} md={4} className="mb-4 mb-md-0">
                <Card style={styles.serviceCard} className="h-100">
                  <Card.Body className="text-center d-flex flex-column">
                    <FontAwesomeIcon
                      icon={item.iconName}
                      size="3x"
                      style={styles.serviceIcon}
                    />
                    <Card.Title style={styles.serviceName}>
                      {item.name}
                    </Card.Title>
                    <Card.Text style={styles.serviceDescription}>
                      {item.description}
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <Row className="text-center mt-5">
            <Col>
              <Button variant="outline-light" as={Link} to="/services">
                Explore all services
              </Button>
            </Col>
          </Row>
        </Container>
      </Container>

      {/* Featured project */}
      <Container fluid style={styles.sectionDarker}>
        <Container>
          <Row className="align-items-center">
            <Col xs={12} md={6} className="mb-4 mb-md-0">
              <p style={styles.eyebrow}>Featured project</p>
              <h2 style={styles.sectionTitleLeft}>VIP Vegas+</h2>
              <p style={styles.featuredText}>
                A full-featured iOS and Android app built for VIP Global UK —
                including a custom API, database, and content management
                system. Designed, developed, and shipped to both app stores by
                CVDS.
              </p>
              <div style={styles.storeBadges}>
                <a
                  href="https://apps.apple.com/us/app/vip-vegas/id6444873810"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image src={appStoreImage} style={styles.appStoreBadge} alt="Download on the App Store" />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.crossvisiondevstudios.vipvegas"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Image src={playStoreImage} style={styles.playStoreBadge} alt="Get it on Google Play" />
                </a>
              </div>
              <Button variant="outline-light" as={Link} to="/portfolio">
                View full portfolio
              </Button>
            </Col>
            <Col xs={12} md={6} className="text-center">
              <Image fluid src={vvpImage} style={styles.featuredImage} alt="VIP Vegas+ app screenshot" />
            </Col>
          </Row>
        </Container>
      </Container>

      {/* Client logos */}
      <Container fluid style={styles.sectionDark}>
        <Container>
          <Row className="text-center mb-4">
            <p style={styles.eyebrow}>Trusted by</p>
          </Row>
          <Row className="align-items-center justify-content-center">
            {clientLogos.map((client) => (
              <Col
                key={client.id}
                xs={6}
                md={3}
                className="text-center mb-4 mb-md-0"
              >
                <Image
                  src={client.image}
                  alt={client.alt}
                  style={{ height: client.height, maxWidth: "100%", objectFit: "contain" }}
                />
              </Col>
            ))}
          </Row>
        </Container>
      </Container>
    </>
  );
};

export default Home;

const styles = {
  appStoreBadge: {
    height: 48,
    marginRight: 16,
  },
  eyebrow: {
    color: "#CF3620",
    fontSize: 14,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 2,
    marginBottom: 8,
  },
  featuredButtons: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  featuredImage: {
    maxHeight: 480,
    borderRadius: 12,
  },
  featuredText: {
    color: "#ACACAC",
    fontSize: 18,
    lineHeight: 1.6,
    marginBottom: 24,
  },
  highlightIcon: {
    color: "#CF3620",
    width: 20,
    flexShrink: 0,
  },
  highlightList: {
    marginBottom: 24,
  },
  highlightRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "6px 0",
    textAlign: "left",
  },
  highlightText: {
    color: "#E6E6E6",
    fontSize: 16,
  },
  heroButtons: {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  heroContainer: {
    paddingTop: 40,
    paddingBottom: 40,
  },
  heroSubtitle: {
    color: "#E6E6E6",
    fontSize: 20,
    lineHeight: 1.6,
    marginBottom: 32,
    textShadow: "0 1px 8px rgba(0, 0, 0, 0.8)",
  },
  heroTitle: {
    color: "#FFF",
    fontSize: "clamp(2rem, 5vw, 3.5rem)",
    fontWeight: 800,
    marginBottom: 20,
    textShadow: "0 2px 12px rgba(0, 0, 0, 0.8)",
  },
  playStoreBadge: {
    height: 48,
  },
  primaryCta: {
    minWidth: 190,
    fontWeight: 600,
  },
  secondaryCta: {
    minWidth: 160,
    fontWeight: 600,
  },
  sectionDark: {
    backgroundColor: "#000",
    paddingTop: 90,
    paddingBottom: 90,
  },
  sectionDarker: {
    backgroundColor: "#12100E",
    paddingTop: 90,
    paddingBottom: 90,
    textAlign: "left",
  },
  sectionSubtitle: {
    color: "#ACACAC",
    fontSize: 18,
  },
  sectionTitle: {
    color: "#FFF",
    fontSize: 38,
    fontWeight: 700,
  },
  sectionTitleLeft: {
    color: "#FFF",
    fontSize: 38,
    fontWeight: 700,
    marginBottom: 16,
  },
  serviceCard: {
    backgroundColor: "#1C1816",
    color: "#FFF",
    border: "1px solid #2E2925",
    borderRadius: 12,
    padding: 16,
  },
  serviceDescription: {
    color: "#ACACAC",
    fontSize: 16,
    lineHeight: 1.6,
  },
  serviceIcon: {
    color: "#CF3620",
    marginBottom: 20,
  },
  serviceName: {
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 12,
  },
  storeBadges: {
    display: "flex",
    alignItems: "center",
    marginBottom: 24,
  },
};
